import { Challenge, Channel, ChannelPhase, Idea } from '@frontend/data-access';
import React from 'react';
import { DraggableLocation, DropResult } from '@hello-pangea/dnd';

export const reorder = (
  list: number[],
  startIndex: number,
  endIndex: number
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const reorderItem = (
  list: Idea[],
  startIndex: number,
  endIndex: number
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const reorderState = ({
  channelPhases,
  source,
  destination,
}: {
  channelPhases: ChannelPhase[];
  source: DraggableLocation;
  destination: DraggableLocation;
}) => {
  const current = channelPhases.find(
    (channelPhase) => channelPhase.phase.id === source.droppableId
  );
  const target = current?.ideas.items[source.index];

  if (current && target) {
    // moving to same list
    if (source.droppableId === destination.droppableId) {
      const reordered = reorderItem(
        current.ideas.items,
        source.index,
        destination.index
      );
      return channelPhases.map((channelPhase) =>
        channelPhase.phase.id === source.droppableId
          ? { ...channelPhase, ideas: reordered }
          : channelPhase
      );
    }

    // moving to different list
    return channelPhases.map((channelPhase) => {
      if (channelPhase.phase.id === source.droppableId) {
        const currentItems = [...channelPhase.ideas.items];
        currentItems.splice(source.index, 1);
        return {
          ...channelPhase,
          ideas: {
            total: channelPhase.ideas.total - 1,
            items: currentItems,
          },
        };
      }
      if (channelPhase.phase.id === destination.droppableId) {
        const nextItems = [...channelPhase.ideas.items];
        nextItems.splice(destination.index, 0, target);
        return {
          ...channelPhase,
          ideas: {
            total: channelPhase.ideas.total + 1,
            items: nextItems,
          },
        };
      }
      return channelPhase;
    });
  }
  return;
};

export const stageDataTransformer = (
  dataSource: Channel,
  itemIds: React.Key[],
  sourceId: string,
  destinationId: string
): Channel => {
  const target = dataSource.channelPhases
    .find((channelPhase) => channelPhase.phase.id === sourceId)
    ?.ideas.items.filter((idea) => itemIds.includes(idea.id));
  const updatedPhase = dataSource.channelPhases.map((channelPhase) => {
    if (channelPhase.phase.id === sourceId) {
      const currentItems = [...channelPhase.ideas.items].filter(
        (idea) => !itemIds.includes(idea.id)
      );
      return {
        ...channelPhase,
        ideas: {
          total: channelPhase.ideas.total - 1,
          items: currentItems,
        },
      };
    }
    if (channelPhase.phase.id === destinationId) {
      const nextItems = [...channelPhase.ideas.items];
      return {
        ...channelPhase,
        ideas: {
          total: channelPhase.ideas.total + 1,
          items: nextItems.concat(target as Idea[]),
        },
      };
    }
    return channelPhase;
  });

  return { ...dataSource, channelPhases: updatedPhase };
};

export const handleDragEnd = async (
  result: DropResult,
  dataSource: Channel
) => {
  const { source, destination } = result;
  if (!destination) {
    return;
  }
  if (
    //check if idea is moving
    source.droppableId === destination.droppableId &&
    source.index === destination.index
  ) {
    return;
  }
  if (source.droppableId === destination.droppableId) {
    return;
  }
  if (result.type === 'COLUMN') {
    //move stages logic
    return;
  }
  const orderedPhases = reorderState({
    channelPhases: dataSource.channelPhases,
    source,
    destination,
  });
  return { ...dataSource, channelPhases: orderedPhases as ChannelPhase[] };
};

export const setSelected = (
  dataSource: Challenge,
  selectedIds: React.Key[]
) => {
  return dataSource;
};
